import type { ColumnDef } from "@blis/insights/entities/types";
import {
  applyExploreDatePreset,
  defaultExploreFilterState,
  type ExploreFilterState,
  formatExploreDateRangeLabel,
  matchesExploreDatePreset,
} from "@blis/insights/explore/explore-filter-state";
import type { Filter, FilterOp } from "@blis/insights/explore/types";

export type ExploreFilterChip = {
  id: string;
  label: string;
  onRemove?: () => void;
};

export function buildExploreFilterChips(
  state: ExploreFilterState,
  columns: readonly ColumnDef[],
  _defaultDateField: string,
  handlers: {
    onDateReset: () => void;
    onIncludeCancelledChange: (include: boolean) => void;
    onRemoveColumnFilter: (field: string) => void;
    onClearAll: () => void;
  },
): ExploreFilterChip[] {
  const labelByField = new Map(columns.map((c) => [c.field, c.label]));
  const chips: ExploreFilterChip[] = [];

  if (!matchesExploreDatePreset(state, "30")) {
    chips.push({
      id: "date-range",
      label: formatExploreDateRangeLabel(state),
      onRemove: handlers.onDateReset,
    });
  }

  if (state.includeCancelled) {
    chips.push({
      id: "include-cancelled",
      label: "Including cancelled results",
      onRemove: () => handlers.onIncludeCancelledChange(false),
    });
  }

  for (const filter of state.columnFilters) {
    const label = labelByField.get(filter.field) ?? filter.field;
    chips.push({
      id: `column:${filter.field}`,
      label: describeColumnFilter(label, filter, columns),
      onRemove: () => handlers.onRemoveColumnFilter(filter.field),
    });
  }

  return chips;
}

function describeColumnFilter(
  label: string,
  filter: Filter,
  columns: readonly ColumnDef[],
): string {
  const column = columns.find((c) => c.field === filter.field);
  const opLabel = operatorLabel(filter.op);

  if (filter.op === "isNull") {
    return `${label} is empty`;
  }
  if (filter.op === "isNotNull") {
    return `${label} is not empty`;
  }

  if (filter.op === "in" && Array.isArray(filter.value)) {
    const values = filter.value.map((v) => formatValue(v, column)).join(", ");
    return `${label} is ${values}`;
  }

  if (filter.op === "eq" && column?.type === "boolean") {
    return `${label} is ${filter.value === true ? "Yes" : "No"}`;
  }

  const valueText = formatValue(filter.value, column);
  if (filter.op === "contains") {
    return `${label} contains “${valueText}”`;
  }
  if (filter.op === "eq") {
    return `${label} is ${valueText}`;
  }

  return `${label} ${opLabel} ${valueText}`;
}

function operatorLabel(op: FilterOp): string {
  switch (op) {
    case "contains":
      return "contains";
    case "startsWith":
      return "starts with";
    case "endsWith":
      return "ends with";
    case "gt":
      return ">";
    case "gte":
      return "≥";
    case "lt":
      return "<";
    case "lte":
      return "≤";
    case "neq":
      return "is not";
    default:
      return op;
  }
}

function formatValue(value: unknown, column: ColumnDef | undefined): string {
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (column?.enumValues) {
    return String(value);
  }
  return String(value ?? "");
}

export function hasActiveExploreFilters(state: ExploreFilterState): boolean {
  return (
    !matchesExploreDatePreset(state, "30") ||
    state.includeCancelled ||
    state.columnFilters.length > 0
  );
}

export function resetExploreDateToDefault(
  state: ExploreFilterState,
): ExploreFilterState {
  return applyExploreDatePreset(state, "30");
}

export { defaultExploreFilterState };
